import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { type CreateCppRateCardBody, createCppRateCard } from '~/services/LuxLoyalty';

import { costPerPointQueryKeys } from './costPerPointQueryKeys';

interface CreateCppRateCardParams {
  body: CreateCppRateCardBody;
  /**
   * When `false`, suppresses the "Rate card created successfully" toast.
   * Used during a save-and-activate flow so only the activation outcome (success or
   * error) is surfaced to the user, instead of a misleading success toast followed
   * by an activation error toast. Defaults to showing the toast (`true`).
   */
  showSuccessToast?: boolean;
  /**
   * When `false`, suppresses the "Error creating rate card" toast.
   * Used by the rate card dialog, which surfaces mutation errors inline (MUI Alert)
   * instead of via snackbar. Defaults to showing the toast (`true`).
   */
  showErrorToast?: boolean;
}

/** Creates a CPP rate card (as DRAFT) and invalidates the list/detail caches. */
export function useCreateCppRateCard() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ body }: CreateCppRateCardParams) => {
      return await createCppRateCard(body);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: costPerPointQueryKeys.all });
      // Both toast flags default to true (undefined → shown); normalise to a concrete boolean.
      if (variables.showSuccessToast ?? true) {
        enqueueSnackbar('Rate card created successfully', { variant: 'success' });
      }
    },
    onError: (error: Error, variables) => {
      if (variables?.showErrorToast ?? true) {
        enqueueSnackbar(`Error creating rate card: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    },
  });
}
